import React from 'react';
import PublicIcon from '@mui/icons-material/Public';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import './JobMarketInsights.css';

interface JobMarketInsightsProps {
  jobs: any[];
  darkMode: boolean;
  selectedLocation?: {
    city: string;
    country: string;
    costOfLiving: number;
    averageSalary: number;
  } | null;
  onIndustryClick?: (industry: string) => void;
  onEmploymentTypeClick?: (type: string) => void;
  onCountryClick?: (country: string) => void;
}

const JobMarketInsights: React.FC<JobMarketInsightsProps> = ({ 
  jobs, 
  darkMode, 
  selectedLocation,
  onIndustryClick,
  onEmploymentTypeClick,
  onCountryClick
}) => {
  // Calculate market insights
  const totalJobs = jobs.length;
  
  const averageSalary = jobs.length > 0 
    ? Math.round(jobs.reduce((sum, job) => sum + job.salary, 0) / jobs.length)
    : 0;
  
  const remoteJobs = jobs.filter(job => job.remote).length;
  const remotePercentage = totalJobs > 0 ? Math.round((remoteJobs / totalJobs) * 100) : 0;
  
  // Industry distribution
  const industryCount = jobs.reduce((acc, job) => {
    acc[job.industry] = (acc[job.industry] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  
  const topIndustries = Object.entries(industryCount)
    .sort(([,a], [,b]) => (b as number) - (a as number))
    .slice(0, 3);
  
  // Employment type distribution
  const employmentTypes = jobs.reduce((acc, job) => {
    acc[job.employmentType] = (acc[job.employmentType] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  
  // Country distribution
  const countryCount = jobs.reduce((acc, job) => {
    acc[job.country] = (acc[job.country] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  
  const topCountries = Object.entries(countryCount)
    .sort(([,a], [,b]) => (b as number) - (a as number))
    .slice(0, 3);

  // Cost of living calculations
  const averageCostOfLiving = jobs.length > 0 
    ? Math.round(jobs.reduce((sum, job) => sum + (job.costOfLiving || 100), 0) / jobs.length)
    : 100;

  const adjustedSalary = selectedLocation 
    ? Math.round(selectedLocation.averageSalary * (100 / selectedLocation.costOfLiving))
    : Math.round(averageSalary * (100 / averageCostOfLiving));

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getCostOfLivingColor = (index: number) => {
    if (index <= 70) return '#2ecc71'; // Green for low cost
    if (index <= 100) return '#f39c12'; // Orange for medium cost
    return '#e74c3c'; // Red for high cost
  };

  const getCostOfLivingDescription = (index: number) => {
    if (index <= 70) return 'Low Cost';
    if (index <= 100) return 'Moderate Cost';
    return 'High Cost';
  };

  return (
    <div className={`job-market-insights ${darkMode ? 'dark' : 'light'}`}>
      <div className="insights-header">
        <h3>Market Insights</h3>
        <p>Real-time job market analytics</p>
      </div>
      
      <div className="insights-grid">
        <div className="insight-card">
          <div className="insight-value">{totalJobs.toLocaleString()}</div>
          <div className="insight-label">Total Positions</div>
        </div>
        
        <div className="insight-card">
          <div className="insight-value">{formatCurrency(averageSalary)}</div>
          <div className="insight-label">Average Salary</div>
        </div>
        
        <div className="insight-card">
          <div className="insight-value">{remotePercentage}%</div>
          <div className="insight-label">Remote Friendly</div>
        </div>
        
        <div className="insight-card">
          <div 
            className="insight-value"
            style={{ color: getCostOfLivingColor(selectedLocation?.costOfLiving || averageCostOfLiving) }}
          >
            {selectedLocation?.costOfLiving || averageCostOfLiving}
          </div>
          <div className="insight-label">Cost of Living Index</div>
        </div>
      </div>
      
      <div className="insights-details">
        <div className="detail-section">
          <h4>Top Industries</h4>
          <div className="detail-list">
            {topIndustries.map(([industry, count]) => (
              <div 
                key={industry} 
                className="detail-item clickable"
                onClick={() => onIndustryClick && onIndustryClick(industry)}
                style={{ cursor: onIndustryClick ? 'pointer' : 'default' }}
              >
                <div className="detail-name">{industry}</div>
                <div className="detail-count">{count as number} jobs</div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="detail-section">
          <h4>Employment Types</h4>
          <div className="detail-list">
            {Object.entries(employmentTypes)
              .sort(([,a], [,b]) => (b as number) - (a as number))
              .slice(0, 3)
              .map(([type, count]) => (
                <div 
                  key={type} 
                  className="detail-item clickable"
                  onClick={() => onEmploymentTypeClick && onEmploymentTypeClick(type)}
                  style={{ cursor: onEmploymentTypeClick ? 'pointer' : 'default' }}
                >
                  <div className="detail-name">{type.charAt(0).toUpperCase() + type.slice(1)}</div>
                  <div className="detail-count">{count as number} jobs</div>
                </div>
              ))}
          </div>
        </div>
        
        <div className="detail-section">
          <h4>Top Locations</h4>
          <div className="detail-list">
            {topCountries.map(([country, count]) => (
              <div 
                key={country} 
                className="detail-item clickable"
                onClick={() => onCountryClick && onCountryClick(country)}
                style={{ cursor: onCountryClick ? 'pointer' : 'default' }}
              >
                <div className="detail-name">{country}</div>
                <div className="detail-count">{count as number} jobs</div>
              </div>
            ))}
          </div>
        </div>
        
        {selectedLocation && (
          <div className="detail-section">
            <h4>Cost of Living - {selectedLocation.city}</h4>
            <div className="detail-list">
              <div className="detail-item">
                <div className="detail-name">Cost Index</div>
                <div 
                  className="detail-count"
                  style={{ 
                    backgroundColor: getCostOfLivingColor(selectedLocation.costOfLiving),
                    color: 'white'
                  }}
                >
                  {selectedLocation.costOfLiving}
                </div>
              </div>
              <div className="detail-item">
                <div className="detail-name">Market Classification</div>
                <div className="detail-count">
                  {getCostOfLivingDescription(selectedLocation.costOfLiving)}
                </div>
              </div>
              <div className="detail-item">
                <div className="detail-name">Purchasing Power</div>
                <div className="detail-count">
                  {formatCurrency(adjustedSalary)}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <div className="market-trends">
        <h4>Market Trends</h4>
        <div className="trend-indicators">
          <div className="trend-item">
            <div className="trend-icon"><TrendingUpIcon style={{ fontSize: '16px' }} /></div>
            <div className="trend-text">High demand in Technology sector</div>
          </div>
          <div className="trend-item">
            <div className="trend-icon"><PublicIcon style={{ fontSize: '16px' }} /></div>
            <div className="trend-text">Remote work opportunities growing</div>
          </div>
          <div className="trend-item">
            <div className="trend-icon"><AttachMoneyIcon style={{ fontSize: '16px' }} /></div>
            <div className="trend-text">Competitive salaries in major tech hubs</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobMarketInsights;